"use client";

import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { getHomepageSectionDefinition } from "../../content/homepageSections";
import { useHomepageSectionContent } from "../../hooks/useHomepageSectionContent";
import resolveImageUrl from "../../utils/resolveImageUrl";
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import HeroCarousel from "../../features/hero/HeroCarousel";

type HeroContent = {
  heroMode?: "video" | "slider";
  brandName: string;
  title: string;
  subtitle: string;
  supportTitle: string;
  supportCopy: string;
  backgroundImage: string;
  videoUrl: string;
};

function normalizeRichText(value: string) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (/<\/?[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return trimmed
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${line}</p>`)
    .join("");
}

function stripHtml(value: string) {
  return String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fallback = getHomepageSectionDefinition("hero")?.defaultPayload as HeroContent;
  const { content } = useHomepageSectionContent<HeroContent>("hero", fallback);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [showVideoCaptions, setShowVideoCaptions] = useState(false);

  const heroPosterSrc = resolveImageUrl(content.backgroundImage || fallback.backgroundImage || "");
  const heroVideoSrc = resolveMediaUrl(content.videoUrl || fallback.videoUrl || "");
  const supportCopyHtml = normalizeRichText(content.supportCopy || fallback.supportCopy || "");
  const heroMode = String(content.heroMode || fallback.heroMode || "video").toLowerCase();
  const previewHeroMode =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("hero-preview")
      : null;
  const effectiveHeroMode =
    previewHeroMode === "video" || previewHeroMode === "slider" ? previewHeroMode : heroMode;
  const useHeroSlider = effectiveHeroMode === "slider";
  const captionsSrc =
    typeof window !== "undefined"
      ? new URL("/hero/video-captions.vtt", window.location.origin).toString()
      : "/hero/video-captions.vtt";

  useEffect(() => {
    setImageLoaded(false);
    setVideoReady(false);
  }, [heroPosterSrc, heroVideoSrc]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const syncCaptions = () => setShowVideoCaptions(mediaQuery.matches);
    syncCaptions();
    mediaQuery.addEventListener("change", syncCaptions);
    return () => mediaQuery.removeEventListener("change", syncCaptions);
  }, []);

  useEffect(() => {
    if (!videoRef.current || !videoReady) return;

    if (isPlaying) {
      void videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying, videoReady]);

  const toggleVideo = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      void videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const videoHero = (
    <section className="relative h-screen min-h-[760px] w-full overflow-hidden bg-[#1b1715]">
      {/* BASE IMAGE */}
      <img
        src={heroPosterSrc}
        alt="Pearl Voyages Travel Tour operator in Uganda, Europe, East Africa and beyond"
        onLoad={() => setImageLoaded(true)}
        className={[
          "absolute inset-0 h-full w-full object-cover transition-[filter,transform] duration-[1400ms] ease-out motion-reduce:transition-none",
          imageLoaded ? "scale-100 blur-0" : "scale-[1.08] blur-[10px]",
        ].join(" ")}
      />

      {/* SOFT LOADING VEIL */}
      {!imageLoaded && (
        <div className="absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02),rgba(0,0,0,0.18))] opacity-55 backdrop-blur-md transition-opacity duration-900 ease-out" />
      )}

      {/* VIDEO LAYER */}
      <video
        ref={videoRef}
        muted
        loop
        autoPlay
        playsInline
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload noplaybackrate noremoteplayback"
        poster={heroPosterSrc}
        className={[
          "hero-video absolute inset-0 z-[2] h-full w-full object-cover transition-opacity duration-[1100ms] ease-out motion-reduce:transition-none",
          videoReady ? "opacity-100" : "opacity-0",
        ].join(" ")}
        onCanPlay={() => setVideoReady(true)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onContextMenu={(e) => e.preventDefault()}
      >
        {heroVideoSrc ? <source src={heroVideoSrc} type="video/mp4" /> : null}
        {showVideoCaptions ? (
          <track
            kind="captions"
            srcLang="en"
            label="English captions"
            src={captionsSrc}
            default
          />
        ) : null}
      </video>

      {/* ATMOSPHERIC OVERLAY */}
      <div className="absolute inset-0 z-[3] bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.18),rgba(0,0,0,0.45)_55%,rgba(0,0,0,0.84)_100%)]" />

      {/* OPTIONAL SHIMMER WHILE LOADING */}
      {!imageLoaded || !videoReady ? (
        <div className="absolute inset-0 z-[4] bg-[linear-gradient(110deg,transparent_20%,rgba(255,255,255,0.08)_45%,transparent_70%)] bg-[length:200%_100%] opacity-35 animate-[shimmer_2.8s_linear_infinite]" />
      ) : null}

      {/* MAIN CONTENT */}
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-[#f8f3ea]">
      <p
        className="mb-5 text-xs font-semibold uppercase tracking-luxury text-[#f8f3ea]/92 drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)] motion-safe:animate-[heroFadeUp_0.8s_ease-out_both]"
      >
        {content.brandName}
      </p>

        <h1
          className="max-w-[1200px] font-serif text-5xl font-semibold leading-[1.06] tracking-tight text-[#f8f3ea] drop-shadow-[0_8px_28px_rgba(0,0,0,0.9)] motion-safe:animate-[heroFadeUp_1s_ease-out_0.2s_both] md:text-7xl lg:text-8xl"
        >
          {content.title}
        </h1>

        <p
          className="mt-4 mb-24 max-w-xl text-base leading-7 text-[#f8f3ea]/90 drop-shadow-[0_4px_18px_rgba(0,0,0,0.8)] motion-safe:animate-[heroFadeUp_1s_ease-out_0.4s_both]"
        >
          {content.subtitle}
        </p>
      </div>

      {/* BOTTOM CONTROL + INFO */}
      <div className="absolute inset-x-0 bottom-10 z-20 px-5 md:bottom-12 md:px-8 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="flex items-center">
            <div className="h-px flex-1 bg-white/30" />

            <div className="mx-5 flex shrink-0 flex-col items-center md:mx-8">
              <div className="mb-3 h-2.5 w-2.5 rounded-full border border-white/60 bg-transparent" />
              <button
                type="button"
                onClick={toggleVideo}
                className="relative flex h-20 w-20 items-center justify-center rounded-full border border-white/35 bg-black/20 backdrop-blur-md transition duration-300 hover:bg-white/10 md:h-24 md:w-24"
                aria-label={isPlaying ? "Pause hero video" : "Play hero video"}
              >
                <span className="absolute inset-0 scale-[1.22] rounded-full border border-white/20" />
                <span className="absolute inset-0 scale-[1.42] rounded-full border border-white/10" />
                {isPlaying ? (
                  <Pause className="h-5 w-5 text-white md:h-6 md:w-6" />
                ) : (
                  <Play className="ml-0.5 h-5 w-5 text-white md:h-6 md:w-6" />
                )}
              </button>
            </div>

            <div className="h-px flex-1 bg-white/30" />
          </div>

          <div className="mt-5 flex flex-col items-center gap-3 text-[#f8f3ea]/88 md:mt-6 md:items-start">
            <div className="text-center text-xs font-semibold uppercase tracking-luxury drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)] md:text-left">
              {stripHtml(content.supportTitle)}
            </div>

            <div
              className="mx-auto max-w-[420px] text-center text-base leading-7 drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)] md:mx-0 md:text-left [&_p]:m-0 [&_p+p]:mt-3"
              dangerouslySetInnerHTML={{ __html: supportCopyHtml }}
            />
          </div>
        </div>
      </div>
    </section>
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = {
      event: "hero_mode_rendered",
      heroMode: effectiveHeroMode,
      pathname: window.location.pathname,
    };
    const win = window as Window & {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: Array<Record<string, unknown>>;
    };
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push(payload);
    if (typeof win.gtag === "function") {
      win.gtag("event", "hero_mode_rendered", {
        hero_mode: effectiveHeroMode,
        page_path: window.location.pathname,
      });
    }
  }, [effectiveHeroMode]);

  if (useHeroSlider) {
    return <HeroCarousel fallback={videoHero} />;
  }

  return videoHero;
}
